'use client';
import { useLocalStorageState, useSetState } from 'ahooks';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import CreateGuide from '@/app/assets/imgs/create_guide.svg';
import { BaseFillLock } from '@/app/components/icons/BaseFillLock';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineShare } from '@/app/components/icons/BaseLineShare';
import { Gift } from '@/app/components/icons/Gift';
import { type FollowResult, useFollow } from '@/app/components/shared/Community';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { FollowButton } from '@/app/components/shared/FollowButton';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { openAlertDialog } from '@/app/components/shared/imperative-dialog';
import { Medal } from '@/app/components/shared/Medal';
import { Navs } from '@/app/components/shared/Nav';
import { UserAvatar } from '@/app/components/shared/UserAvatar';
import { Button } from '@/app/components/ui/button';
import { SITE } from '@/app/constants';
import { useCopy, useFetch, useReportPageTab } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { apiGetMyPersonalInfo, apiGetOtherPersonalInfo } from '../apis';
import {
  COMMUNITY_SEARCH_OPTIONS,
  DEFAULT_SHOWN_MEDAL_IDS,
  LIKES_SUBTYPE,
  PROFILE_TAB_ENUM,
  STAT_CONFIGS,
} from '../constant';
import type { PersonalInfo, ProfileState, ProfileTabItem } from '../types';
import { Collected } from './Collected';
import { Likes } from './Likes';
import { Published } from './Published';
import { StatDisplay } from './StatDisplay';
import { StatModal } from './StatModal';
import { UserProfile } from './UserProfile';

interface ProfileProps {
  userId?: string;
}

export const Profile = ({ userId }: ProfileProps) => {
  const t = useTranslations();
  const pathname = usePathname();
  const { copy } = useCopy();

  const urlUpdatedRef = useRef(false);
  const searchParams = useSearchParams();
  const [objType, setObjType] = useState<string | undefined>(undefined);
  const [objId, setObjId] = useState<string | undefined>(undefined);

  const { userInfo, isUserInfoInitialized } = useStore((state) => ({
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
  }));

  // 处理 URL 参数
  useEffect(() => {
    // 如果登录状态未初始化或用户未登录，不处理参数
    if (!isUserInfoInitialized || !userInfo?.id) {
      return;
    }

    const objId = searchParams.get('obj_id');
    const objType = searchParams.get('obj_type');
    const userMedalType = searchParams.get('user_medal_type');

    if (objId || objType) {
      setObjId(objId || undefined);
      setObjType(objType || undefined);
      window.history.replaceState(null, '', pathname);
    }

    // 处理勋章类型参数
    if (userMedalType) {
      setObjType(userMedalType);
      window.history.replaceState(null, '', pathname);
    }
  }, [searchParams, pathname, isUserInfoInitialized, userInfo?.id]);

  const [profileState, setProfileState] = useSetState<ProfileState>({
    active: PROFILE_TAB_ENUM.PUBLISHED,
    personalInfo: null,
    isMe: false,
    dialogOpen: false,
    openSettingFrom: 'other',
    ready: false,
    errorMsg: '',
    selectedStatKey: undefined,
  });

  useEffect(() => {
    if (userInfo?.id && !urlUpdatedRef.current) {
      const isMe = (userId && userInfo?.id === userId) || !userId;
      // 如果是本人访问且URL中没有userId，静默更新URL
      if (isMe && !userId) {
        const currentUrl = new URL(window.location.href);
        const newUrl = `${currentUrl.pathname}/${userInfo.id}${currentUrl.search}`;
        window.history.replaceState(null, '', newUrl);
        urlUpdatedRef.current = true;
      }
    }
  }, [userId, userInfo?.id]);

  const reportClick = (pos: string) => {
    reportEvent('profile_edition_click', {
      element_name: pos,
    });
  };

  useReportPageTab(profileState.active);

  const tabs = useMemo(
    () => [
      {
        key: PROFILE_TAB_ENUM.PUBLISHED,
        label: SITE === 'china' ? '投稿作品(已审核)' : t('profile.published'),
      },
      {
        key: PROFILE_TAB_ENUM.FAVORITE,
        label: t('profile.like'),
        hidden: !profileState.isMe,
        activeIcon: (
          <CustomTooltip side="top" content={t('profile.likeIcon.tip')}>
            <div>
              <BaseFillLock className="size-[18px] sm:size-3" />
            </div>
          </CustomTooltip>
        ),
        inActiveIcon: (
          <CustomTooltip side="top" content={t('profile.likeIcon.tip')}>
            <div>
              <BaseFillLock className="size-[18px] opacity-65 sm:size-3" />
            </div>
          </CustomTooltip>
        ),
      },
      {
        key: PROFILE_TAB_ENUM.COLLECT,
        label: t('profile.collected'),
        hidden: !profileState.isMe,
        activeIcon: (
          <CustomTooltip side="top" content={t('profile.collectIcon.tip')}>
            <div>
              <BaseFillLock className="size-[18px] sm:size-3" />
            </div>
          </CustomTooltip>
        ),
        inActiveIcon: (
          <CustomTooltip side="top" content={t('profile.collectIcon.tip')}>
            <div>
              <BaseFillLock className="size-[18px] opacity-65 sm:size-3" />
            </div>
          </CustomTooltip>
        ),
      },
    ],
    [profileState.isMe],
  );

  const { run: fetchPersonalInfo } = useFetch(
    (userId?: string) => (userId ? apiGetOtherPersonalInfo : apiGetMyPersonalInfo)(userId),
    {
      manual: true,
      onSuccess: ({ data, error }, params) => {
        if (error) {
          setProfileState({
            ready: true,
            errorMsg: error.reason === 'NotFound' ? t('profile.logout.tip') : t('popup.dialog.error.system.content'),
          });
          return;
        }
        setProfileState({
          personalInfo: data,
          ready: true,
          isMe: !params[0],
        });
      },
    },
  );

  useEffect(() => {
    if (userInfo?.id) {
      const isMe = (userId && userInfo?.id === userId) || !userId;
      fetchPersonalInfo(!isMe ? userId : undefined);
    }
  }, [userId, userInfo?.id, fetchPersonalInfo]);

  const personalInfo = useMemo(() => {
    return {
      stat: profileState.personalInfo?.stat,
      medal: profileState.personalInfo?.medal,
      user: profileState.isMe
        ? userInfo
        : {
            ...profileState.personalInfo?.user,
            id: profileState.personalInfo?.user?.user_id || '',
            nickname: profileState.personalInfo?.user?.nick_name || '',
          },
    };
  }, [profileState.personalInfo, profileState.isMe, userInfo]);

  // 已显示过的勋章
  const [shownMedalIds, setShownMedalIds] = useLocalStorageState<string[]>(`shown_medal_ids_${userInfo?.id || ''}`, {
    defaultValue: DEFAULT_SHOWN_MEDAL_IDS,
  });

  const firstTimeMedal = useMemo(() => {
    if (!profileState.isMe || !personalInfo.medal || personalInfo.medal.length === 0) {
      return null;
    }

    const newMedal = personalInfo.medal.find((medal) => {
      if (!medal.user_medal_type || medal.user_medal_type === 'unspecified') {
        return false;
      }
      return !shownMedalIds.includes(medal.user_medal_type);
    });

    return newMedal || null;
  }, [personalInfo.medal, profileState.isMe, shownMedalIds]);

  // 首次获得勋章时自动弹出（仅在个人主页且没有URL参数时）
  useEffect(() => {
    if (firstTimeMedal && profileState.isMe && profileState.ready && !objType && firstTimeMedal.user_medal_type) {
      setObjType(firstTimeMedal.user_medal_type);
      setShownMedalIds((prev) => {
        const prevList = prev || [];
        const medalType = firstTimeMedal.user_medal_type;
        if (medalType && !prevList.includes(medalType)) {
          return [...prevList, medalType];
        }
        return prevList;
      });
    }
  }, [firstTimeMedal, profileState.isMe, profileState.ready, objType, setShownMedalIds]);

  const defaultSelectedMedal = useMemo(() => {
    return personalInfo.medal?.find((medal) => medal.user_medal_type === objType);
  }, [personalInfo.medal, objType]);

  /**
   * 处理关注状态变化
   */
  const handleFollowChange = (result: FollowResult, updateUserFollowing = true) => {
    const newPersonalInfo = {
      ...profileState.personalInfo,
      ...(updateUserFollowing && {
        user: { ...profileState.personalInfo?.user, is_following: result.followed },
      }),
    };

    if (profileState.isMe) {
      // 自己的页面：更新关注数
      newPersonalInfo.stat = {
        ...profileState.personalInfo?.stat,
        following_count: result.followed
          ? (profileState.personalInfo?.stat?.following_count ?? 0) + 1
          : (profileState.personalInfo?.stat?.following_count ?? 0) - 1,
      };
    } else {
      // 别人的页面：更新粉丝数
      newPersonalInfo.stat = {
        ...profileState.personalInfo?.stat,
        follower_count: result.followed
          ? (profileState.personalInfo?.stat?.follower_count ?? 0) + 1
          : (profileState.personalInfo?.stat?.follower_count ?? 0) - 1,
      };
    }

    setProfileState({
      personalInfo: newPersonalInfo,
    });
  };

  /**
   * 关注 / 取消关注
   */
  const { handleFollow, state: followState } = useFollow(personalInfo.user?.id || '', {
    initialIsFollowing: (personalInfo.user as PersonalInfo['user'])?.is_following || false,
    onFollowChange: (result) => handleFollowChange(result, true),
  });

  /**
   * 分享
   */
  const handleShareClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const url = `${window.location.origin}/profile/${personalInfo.user?.id}`;
    copy(url, {
      hideMessage: true,
    });
    openAlertDialog({
      buttonText: t('elements.deleteElement.rightBtn'),
      closable: false,
      description: t('create.share.description'),
    });
  };

  const handleExpertClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    window.open(`${window.location.origin}/collaboration`, '_blank');
  };

  if (!profileState.ready) {
    return (
      <div className="my-10">
        <GlobalLoader />
      </div>
    );
  }

  if (profileState.errorMsg) {
    return (
      <div className="flex h-[calc(100vh-220px)] items-center justify-center">
        <div className="flex h-full w-[400px] flex-col items-center justify-center lg:w-[320px]">
          <img
            src={CreateGuide.src}
            alt="create_guide"
            className="mb-8 w-[400px] opacity-60 sm:w-[200px] lg:mb-6 lg:w-[320px]"
          />
          <div className="mb-10 text-center">{profileState.errorMsg}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-content flex min-h-full flex-col">
      <div
        className={cn(
          'relative mb-6 flex shrink-0 items-center gap-6 overflow-hidden border-system-white20 border-b-[0.5px] pb-6 sm:mt-2 sm:flex-col sm:justify-center sm:gap-0 sm:border-none sm:p-0',
          profileState.isMe && 'cursor-pointer',
        )}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.tagName === 'DIV' && target.hasAttribute('data-state')) return;

          if (profileState.isMe) {
            if (e.target === e.currentTarget) {
              reportClick('background');
            }
            setProfileState({
              dialogOpen: true,
            });
          }
        }}
      >
        <UserAvatar
          className="size-[86px] flex-shrink-0 sm:size-16"
          userInfo={{
            id: personalInfo.user?.id || '',
            avatar: personalInfo.user?.avatar || '',
            nickname: personalInfo.user?.nickname || '',
            role: 'user',
            gender: 'unspecified',
            created_at: '',
          }}
          onClick={() => {
            if (profileState.isMe) {
              reportClick('head');
              setProfileState({
                openSettingFrom: 'head',
              });
            }
          }}
        />
        <div className="flex flex-1 flex-col gap-2 sm:mt-2 sm:w-full sm:gap-1">
          <div className="flex items-center gap-2 sm:justify-center">
            <CustomTooltip content={personalInfo.user?.nickname ?? '--'} maxLines={1} showOnlyOnOverflow>
              <div
                className="font-medium text-xl sm:max-w-[200px]"
                onClick={() => {
                  if (profileState.isMe) {
                    reportClick('user_name');
                    setProfileState({
                      openSettingFrom: 'user_name',
                    });
                  }
                }}
              >
                {personalInfo.user?.nickname ?? '--'}
              </div>
            </CustomTooltip>
            <CustomTooltip
              content={
                <div className="flex items-center gap-1">
                  {personalInfo.user?.id ?? ''}
                  <BaseLineCopy
                    className="cursor-pointer"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      copy(personalInfo.user?.id ?? '');
                    }}
                  />
                </div>
              }
            >
              <div className="flex size-6 cursor-pointer items-center justify-center rounded-4 bg-system-white/10 text-sm text-system-white/70">
                ID
              </div>
            </CustomTooltip>
            <Medal
              medals={personalInfo.medal}
              size="large"
              owner={personalInfo.user}
              isProfilePage
              defaultSelectedMedal={defaultSelectedMedal}
              isMe={profileState.isMe}
            />
          </div>

          {(profileState.isMe || !!personalInfo.user?.biography) && (
            <CustomTooltip
              content={
                personalInfo.user?.biography ||
                (profileState.isMe ? t('profile.biography.edit') : t('profile.biography.placeholder'))
              }
              showOnlyOnOverflow
              maxLines={1}
            >
              <div
                className={cn('text-system-white/70 text-xs sm:text-center', profileState.isMe && 'cursor-pointer')}
                onClick={() => {
                  if (profileState.isMe) {
                    reportClick('introduction');
                    setProfileState({
                      openSettingFrom: 'introduction',
                    });
                  }
                }}
              >
                {personalInfo.user?.biography ||
                  (profileState.isMe ? t('profile.biography.edit') : t('profile.biography.placeholder'))}
              </div>
            </CustomTooltip>
          )}

          <StatDisplay
            statConfigs={STAT_CONFIGS({ handleClickStat: (selectedStatKey) => setProfileState({ selectedStatKey }) })}
            statData={personalInfo.stat}
            isMe={profileState.isMe}
          />
        </div>
        <div className="flex flex-shrink-0 items-center gap-3 sm:mt-6 sm:w-full sm:flex-row-reverse">
          {profileState.isMe ? (
            <Button
              variant="ghost"
              className="h-8 rounded-8 bg-system-white10 px-3 font-semibold text-sm hover:bg-system-white20 hover:text-system-white sm:h-10 sm:flex-1"
              iconClassName="sm:block"
              icon={<BaseLineEdit className="mr-1 size-[18px] sm:mr-2" />}
            >
              {t('personal.accountSetting')}
            </Button>
          ) : (
            <FollowButton
              className="sm:h-10 sm:flex-1"
              iconClassName="sm:block"
              isFollowing={followState.isFollowing}
              icon={!followState.isFollowing ? <BaseLinePlus className="mr-1 size-[18px] sm:mr-2" /> : null}
              onClick={handleFollow}
            />
          )}
          <Button
            variant="ghost"
            className="h-8 rounded-8 bg-system-white10 px-3 font-semibold text-sm hover:bg-system-white20 hover:text-system-white sm:h-10 sm:flex-1"
            iconClassName="sm:block"
            icon={<BaseLineShare className="mr-1 size-[18px] sm:mr-2" />}
            onClick={handleShareClick}
          >
            {t('profile.share')}
          </Button>
          {profileState.isMe && SITE === 'china' && (
            <Button
              variant="ghost"
              className="h-8 rounded-8 bg-system-white10 px-3 font-semibold text-sm hover:bg-system-white20 hover:text-system-white sm:h-10 sm:flex-1 md:hidden"
              iconClassName="sm:block"
              icon={<Gift className="mr-1 size-[18px] sm:mr-2" />}
              onClick={handleExpertClick}
            >
              达人合作
            </Button>
          )}
        </div>
      </div>
      <div className="sticky top-0 z-20 flex items-center justify-between bg-system-bg08 pb-5 text-xl sm:pb-4">
        <Navs<ProfileTabItem>
          navs={tabs.filter((it) => !it.hidden)}
          active={profileState.active}
          onChange={(it) => {
            if (profileState.active !== it.key) {
              reportEvent('me_page_element_click', {
                element_name: it.key,
                ...(it.key === PROFILE_TAB_ENUM.FAVORITE && {
                  subtype: LIKES_SUBTYPE[COMMUNITY_SEARCH_OPTIONS.ALL],
                }),
              });
              setProfileState({
                active: it.key,
              });
            }
          }}
        />
      </div>

      {profileState.active === PROFILE_TAB_ENUM.PUBLISHED && (
        <Published userId={!profileState.isMe ? userId : undefined} objId={objId} objType={objType} />
      )}
      {profileState.active === PROFILE_TAB_ENUM.COLLECT && <Collected />}
      {profileState.active === PROFILE_TAB_ENUM.FAVORITE && <Likes userId={!profileState.isMe ? userId : undefined} />}

      <UserProfile
        source={profileState.openSettingFrom}
        open={profileState.dialogOpen}
        onClose={() =>
          setProfileState({
            dialogOpen: false,
          })
        }
      />

      {!!profileState.selectedStatKey && (
        <StatModal
          onClose={() => setProfileState({ selectedStatKey: undefined })}
          selectedStatKey={profileState.selectedStatKey}
          onFollowChange={(result) => handleFollowChange(result, false)}
        />
      )}
    </div>
  );
};
